import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { searchKeywordAliases } from "../data/keywordAliases";
import { RiMedicineBottleLine } from "react-icons/ri";
import { MdCategory } from "react-icons/md";
import { FiSearch } from "react-icons/fi";

/**
 * Autocomplete suggestions dropdown for the SearchBar.
 * Shows keyword-alias matches (everyday terms → pharmaceutical categories)
 * as the user types. Appears after 2+ characters, with a 300ms debounce.
 */
export default function SearchSuggestions({ query, isVisible, onSelect, onClose, searchType }) {
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query || query.length < 2 || !isVisible) {
      setSuggestions([]);
      return;
    }

    debounceRef.current = setTimeout(() => {
      const matches = searchKeywordAliases(query);
      setSuggestions(matches);
      setActiveIndex(-1);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, isVisible]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  if (!isVisible || suggestions.length === 0) return null;

  const handleSelectExample = (example) => {
    onSelect(example.name);
    navigate(`/search?q=${encodeURIComponent(example.name)}&type=tradenames&page=1`);
  };

  const handleSeeAll = (suggestion) => {
    onSelect(suggestion.keyword);
    navigate(
      `/search?q=${encodeURIComponent(suggestion.keyword)}&type=${searchType || "all"}&page=1`
    );
  };

  return (
    <div
      ref={containerRef}
      className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-secondary/10 rounded-xl shadow-xl shadow-secondary/8 z-50 overflow-hidden"
      role="listbox"
      aria-label="Search suggestions"
    >
      {suggestions.map((suggestion, groupIdx) => (
        <div
          key={suggestion.keyword}
          className={groupIdx > 0 ? "border-t border-secondary/8" : ""}
        >
          {/* Category header */}
          <div className="px-4 pt-3 pb-1.5 flex items-center gap-2">
            <MdCategory className="size-4 text-primary/70" />
            <span className="text-14 font-text font-medium text-primary">
              {suggestion.category}
            </span>
            <span className="text-14 font-text text-secondary/70 ml-auto">
              {suggestion.description}
            </span>
          </div>

          {/* Example products */}
          <div className="px-2 pb-2">
            {suggestion.examples.slice(0, 4).map((example, i) => (
              <button
                key={example.name}
                onClick={() => handleSelectExample(example)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-primary/5 transition-colors group"
                role="option"
              >
                <RiMedicineBottleLine className="size-4 text-secondary/40 group-hover:text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-16 font-text font-medium text-secondary group-hover:text-primary block">
                    {example.name}
                  </span>
                  <span className="text-14 font-text text-secondary/60">
                    {example.generic}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* See all link */}
          <button
            onClick={() => handleSeeAll(suggestion)}
            className="w-full flex items-center justify-center gap-1.5 px-4 py-3 text-14 font-text font-medium text-primary hover:bg-primary/5 border-t border-secondary/5 transition-colors"
          >
            <FiSearch className="size-4" />
            See all results for "{suggestion.keyword}"
          </button>
        </div>
      ))}
    </div>
  );
}
